import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Pencil, Trash2, Files, Plus, ListChecks } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableBookRow } from "@/components/admin/SortableBookRow";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Navbar } from "@/components/Navbar";
import { MaskLines } from "@/components/Motion";
import { AdminTabs } from "@/components/admin/AdminTabs";
import { BookDialog } from "@/components/admin/BookDialog";
import { BulkUploadDialog } from "@/components/admin/BulkUploadDialog";
import { SampleSlots } from "@/components/admin/SampleSlots";
import { QuizDialog } from "@/components/admin/QuizDialog";
import { QuizVisibility } from "@/components/admin/QuizVisibility";
import { api, errMsg, LEVELS, SUBJECTS } from "@/lib/api";

export default function AdminBooks() {
  const [books, setBooks] = useState([]);
  const [subject, setSubject] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [dialog, setDialog] = useState({ open: false, book: null });
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pct, setPct] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [kind, setKind] = useState("library");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDelete, setBulkDelete] = useState(false);
  const [quizBook, setQuizBook] = useState(null);
  const isSample = kind === "samples";
  const pool = books.filter((b) => Boolean(b.is_sample) === isSample);

  const load = useCallback(() => {
    api.get("/admin/books").then((r) => setBooks(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form, file) => {
    setSaving(true); setPct(0);
    try {
      if (dialog.book) {
        await api.put(`/admin/books/${dialog.book.id}`, { title: form.title, author: form.author, subject: form.subject, level: form.level });
        toast.success("تم حفظ بيانات الكتاب");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append("is_sample", isSample);
        await api.post("/admin/books", fd, { onUploadProgress: (e) => setPct(e.total ? Math.round((e.loaded / e.total) * 100) : 0) });
        toast.success(isSample ? "تم رفع المصدر وأصبح متاحًا في صفحة المصادر المجانية" : "تم رفع الكتاب وأصبح متاحًا في المكتبة");
      }
      setDialog({ open: false, book: null });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/books/${toDelete.id}`);
      toast.success(`تم حذف «${toDelete.title || toDelete.original_filename || "الكتاب"}»`);
      setToDelete(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const subjIdx = (id) => SUBJECTS.findIndex((s) => s.id === id);
  const shown = pool.filter((b) => (subject === "all" || b.subject === subject) && (levelFilter === "all" || b.level === levelFilter))
    .map((b, i) => ({ b, i }))
    .sort((x, y) => subjIdx(x.b.subject) - subjIdx(y.b.subject) || x.b.level - y.b.level || x.i - y.i)
    .map((x) => x.b);
  const openNew = () => setDialog({ open: true, book: null });

  // Selection only counts books visible under the current filters.
  const picked = new Set(shown.filter((b) => selected.has(b.id)).map((b) => b.id));
  const allChecked = shown.length > 0 && picked.size === shown.length;
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(shown.map((b) => b.id)));
  const removeSelected = async () => {
    const ids = [...picked];
    setBulkDelete(false);
    try {
      const { data } = await api.post("/admin/books/bulk-delete", { ids });
      toast.success(`تم حذف ${data?.deleted ?? ids.length} كتاب`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };


  // Drag a book within its own language and level; the library shows books in this order.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const a = pool.find((x) => x.id === active.id);
    const o = pool.find((x) => x.id === over.id);
    if (!a || !o) return;
    if (a.subject !== o.subject || a.level !== o.level) { toast.error("يمكن الترتيب داخل نفس اللغة والمستوى فقط"); return; }
    const group = shown.filter((x) => x.subject === a.subject && x.level === a.level);
    const moved = arrayMove(group, group.findIndex((x) => x.id === a.id), group.findIndex((x) => x.id === o.id));
    let k = 0;
    const order = pool.map((x) => (x.subject === a.subject && x.level === a.level ? moved[k++] : x));
    setBooks((list) => [...list.filter((x) => Boolean(x.is_sample) !== isSample), ...order]);
    try { await api.put("/admin/books/order", { ids: order.map((x) => x.id) }); toast.success("تم حفظ الترتيب"); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };

  const setLevel = async (b, level) => {
    try {
      await api.put(`/admin/books/${b.id}`, { title: b.title, author: b.author || "", subject: b.subject, level });
      setBooks((list) => list.map((x) => (x.id === b.id ? { ...x, level } : x)));
      toast.success(`أصبح الكتاب في المستوى ${level}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]" dir="rtl" data-testid="admin-books-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-sm font-semibold text-[#B45309] mb-2">لوحة الإدارة</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold"><MaskLines lines={["إدارة الكتب"]} delay={0.1} /></h1>
          </div>
          {!isSample && (<div className="flex flex-wrap gap-3">
          <button onClick={() => setBulkOpen(true)} data-testid="admin-bulk-upload-trigger"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#8B3A2B] text-[#8B3A2B] px-6 py-3 font-semibold hover:bg-[#8B3A2B] hover:text-[#FAF7F2] transition-colors">
            <Files className="w-5 h-5" /> {isSample ? "رفع عدة مصادر" : "رفع عدة كتب"}
          </button>
          <button onClick={() => setDialog({ open: true, book: null })} data-testid="admin-upload-book-trigger"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8B3A2B] text-[#FAF7F2] px-6 py-3 font-semibold hover:bg-[#702E22] transition-colors">
            <Upload className="w-5 h-5" /> {isSample ? "رفع مصدر مجاني PDF" : "إضافة كتاب جديد"}
          </button>
          </div>)}
        </div>
        <AdminTabs />

        <div className="inline-flex p-1 mb-6 bg-[#F2ECE1] rounded-full" data-testid="admin-books-kind">
          {[["library", "كتب المكتبة"], ["samples", "المصادر المجانية (معاينة)"]].map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`admin-books-kind-${k}`}
              className={`px-5 h-10 rounded-full text-sm font-semibold transition-colors ${kind === k ? "bg-[#1C1917] text-[#FAF7F2]" : "text-stone-700 hover:text-[#1C1917]"}`}>{l}</button>
          ))}
        </div>
        {isSample && <p className="text-sm text-stone-700 mb-6 -mt-2" data-testid="admin-samples-hint">المصادر المجانية: مساحة لأربعة مصادر لكل لغة (مصدر واحد لكل مستوى). تظهر في صفحة «مصادر مجانية» ويستطيع أي زائر معاينتها دون تسجيل، ولا تظهر في المكتبة.</p>}

        {isSample ? (
          <SampleSlots samples={pool} onChanged={load} onEdit={(b) => setDialog({ open: true, book: b })} onDelete={setToDelete} />
        ) : (<>
        <QuizVisibility />
        <div className="grid grid-cols-2 gap-4 mb-8">
          {SUBJECTS.map((s) => (
            <div key={s.id} className="bg-white border border-[#E5DCCB] p-5" data-testid={`admin-books-count-${s.id}`}>
              <p className="text-sm text-stone-600">{s.ar}</p>
              <p className="font-display text-4xl font-bold">{pool.filter((b) => b.subject === s.id).length}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {LEVELS.map((l) => (
                  <span key={l.n} title={l.name} className="text-[12px] px-2 py-0.5 rounded-full bg-[#F2ECE1]">مستوى {l.n}: {pool.filter((b) => b.subject === s.id && b.level === l.n).length}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {[{ id: "all", ar: "كل المواد" }, ...SUBJECTS].map((s) => (
            <button key={s.id} onClick={() => setSubject(s.id)} data-testid={`admin-books-filter-${s.id}`}
              className={`px-4 h-10 rounded-full text-sm border transition-colors ${subject === s.id ? "bg-[#1C1917] text-[#FAF7F2] border-[#1C1917]" : "bg-white border-[#E5DCCB] hover:border-[#1C1917]"}`}>{s.ar}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="admin-books-level-filters">
          {[{ n: "all", name: "كل المستويات" }, ...LEVELS].map((l) => (
              <button key={l.n} onClick={() => setLevelFilter(l.n)} data-testid={`admin-books-level-${l.n}`}
                className={`px-3.5 h-9 rounded-full text-sm border transition-colors ${levelFilter === l.n ? "bg-[#B45309] text-white border-[#B45309]" : "bg-white border-[#E5DCCB] hover:border-[#B45309]"}`}>
                {l.n === "all" ? l.name : ["مبتدئ", "متوسط", "متقدم", "متقن"][l.n - 1]}
              </button>
          ))}
        </div>
        </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setBulkOpen(true)} data-testid="admin-books-bulk-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#047857] text-[#047857] px-4 h-10 text-sm font-semibold hover:bg-[#047857] hover:text-white transition-colors">
              <Files className="w-4 h-4" /> رفع عدة كتب
            </button>
            <button onClick={openNew} data-testid="admin-books-add-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#047857] text-white px-5 h-10 text-sm font-semibold hover:bg-[#065F46] transition-colors">
              <Plus className="w-4 h-4" /> إضافة كتاب جديد
            </button>
          </div>
        </div>

        {picked.size > 0 && (
          <div className="sticky top-20 z-30 mb-3 flex flex-wrap items-center gap-3 rounded-full bg-[#1C1917] text-white px-5 py-2.5 shadow-lg" data-testid="admin-books-selection-bar">
            <span className="text-sm font-semibold" data-testid="admin-books-selected-count">تم تحديد {picked.size} كتاب</span>
            <button onClick={() => setBulkDelete(true)} data-testid="admin-books-bulk-delete-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-700 hover:bg-red-800 px-4 h-9 text-sm font-semibold transition-colors"><Trash2 className="w-4 h-4" /> حذف المحدد</button>
            <button onClick={() => setSelected(new Set())} data-testid="admin-books-clear-selection"
              className="rounded-full border border-white/40 px-4 h-9 text-sm hover:bg-white/10 transition-colors">إلغاء التحديد</button>
          </div>
        )}
        <div className="bg-white border border-[#E5DCCB] overflow-x-auto" data-lenis-prevent>
          <table className="w-full text-sm min-w-[720px]" data-testid="admin-books-table">
            <thead className="bg-[#F2ECE1] text-stone-700">
              <tr>
                <th className="text-right font-semibold px-4 py-3">الترتيب</th>
                <th className="px-2 py-3 w-10">
                  <input type="checkbox" aria-label="تحديد الكل" checked={allChecked} onChange={toggleAll} disabled={!shown.length}
                    className="w-4 h-4 accent-[#8B3A2B] cursor-pointer" data-testid="admin-books-select-all" />
                </th>
                {["الكتاب", "المادة", "تصنيف المستوى", "الصفحات", "الحجم", "إجراءات"].map((h) => <th key={h} className="text-right font-semibold px-4 py-3">{h}</th>)}
              </tr>
            </thead>
            <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
            <SortableContext items={shown.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <tbody>
              {shown.map((b) => (
                <SortableBookRow key={b.id} id={b.id}>
                  <td className="px-2 py-4">
                    <input type="checkbox" aria-label="تحديد الكتاب" checked={picked.has(b.id)} onChange={() => toggleOne(b.id)}
                      className="w-4 h-4 accent-[#8B3A2B] cursor-pointer" data-testid={`admin-book-select-${b.id}`} />
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-semibold" dir="auto">{b.title || <span className="text-stone-500 font-normal">بدون عنوان</span>}</p>
                    {b.original_filename && <p className="text-xs text-stone-600 mt-0.5" dir="ltr">{b.original_filename}</p>}
                  </td>
                  <td className="px-4 py-4">{SUBJECTS.find((s) => s.id === b.subject)?.ar ?? b.subject}</td>
                  <td className="px-4 py-4">
                    <select value={b.level} onChange={(e) => setLevel(b, Number(e.target.value))} data-testid={`admin-book-level-select-${b.id}`} aria-label="تصنيف المستوى"
                      className="h-9 px-2 rounded-full border-2 border-[#B45309] bg-[#FFFBEB] text-[#7C2D12] font-semibold text-sm focus:outline-none">
                      {LEVELS.map((l) => <option key={l.n} value={l.n}>{`المستوى ${l.n} · ${l.name}`}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-4">{b.page_count}</td>
                  <td className="px-4 py-4" dir="ltr">{((b.size ?? 0) / 1048576).toFixed(1)} MB</td>
                  <td className="px-4 py-4">
                    <div className="flex gap-2">
                      <button onClick={() => setDialog({ open: true, book: b })} data-testid={`admin-edit-book-btn-${b.id}`} className="inline-flex items-center gap-1 px-3 h-9 rounded-full border border-[#1C1917] text-sm font-semibold hover:bg-[#1C1917] hover:text-white transition-colors"><Pencil className="w-4 h-4" /> تعديل</button>
                      {!b.is_sample && (
                        <button onClick={() => setQuizBook(b)} data-testid={`admin-quiz-book-btn-${b.id}`}
                          className={`inline-flex items-center gap-1 px-3 h-9 rounded-full border text-sm font-semibold transition-colors ${b.has_quiz ? "bg-[#047857] border-[#047857] text-white hover:bg-[#065F46]" : "border-[#047857] text-[#047857] hover:bg-[#047857] hover:text-white"}`}>
                          <ListChecks className="w-4 h-4" /> {b.has_quiz ? "الاختبار" : "إضافة اختبار"}
                        </button>
                      )}
                      <button onClick={() => setToDelete(b)} data-testid={`admin-delete-book-btn-${b.id}`} className="inline-flex items-center gap-1 px-3 h-9 rounded-full border border-red-800 text-red-800 text-sm font-semibold hover:bg-red-800 hover:text-white transition-colors"><Trash2 className="w-4 h-4" /> حذف</button>
                    </div>
                  </td>
                </SortableBookRow>
              ))}
            </tbody>
            </SortableContext>
            </DndContext>
          </table>
          <QuizDialog book={quizBook} onClose={() => setQuizBook(null)} onChanged={load} />
          {!shown.length && (
            <div className="p-8 text-center text-stone-600 flex flex-col items-center gap-4" data-testid="admin-books-empty">
              <p>لم تُرفع كتب بعد.</p>
              <button onClick={openNew} data-testid="admin-books-empty-add-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#047857] text-white px-6 py-3 font-semibold hover:bg-[#065F46] transition-colors">
                <Plus className="w-5 h-5" /> إضافة كتاب جديد
              </button>
            </div>
          )}
        </div>
        <p className="text-xs text-stone-600 mt-3">اسحب الكتاب من المقبض (⠇) لتغيير ترتيبه داخل نفس اللغة والمستوى؛ تظهر بهذا الترتيب في المكتبة. تظهر الكتب المرفوعة في المكتبة إلى جانب الكتب التجريبية، ولا يقرؤها إلا أصحاب الحسابات المفعّلة.</p>
        </>)}
      </div>

      <BulkUploadDialog open={bulkOpen} isSample={isSample} onOpenChange={setBulkOpen} onDone={(n) => { if (n) toast.success(`تم رفع ${n} كتاب`); load(); }} />
      <BookDialog open={dialog.open} initial={dialog.book} saving={saving} uploadPct={pct} onSubmit={save} onOpenChange={(o) => !saving && setDialog((d) => ({ ...d, open: o }))} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent dir="rtl" className="bg-[#FAF7F2]" data-testid="admin-delete-book-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right font-display text-2xl">حذف الكتاب؟</AlertDialogTitle>
            <AlertDialogDescription className="text-right">سيُزال «{toDelete?.title || toDelete?.original_filename}» من المكتبة مع علامات القراءة الخاصة به.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel data-testid="admin-delete-book-cancel">إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-800 hover:bg-red-900" data-testid="admin-delete-book-confirm">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDelete} onOpenChange={(o) => !o && setBulkDelete(false)}>
        <AlertDialogContent dir="rtl" className="bg-[#FAF7F2]" data-testid="admin-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right font-display text-2xl">حذف {picked.size} كتاب؟</AlertDialogTitle>
            <AlertDialogDescription className="text-right">ستُزال الكتب المحددة من {isSample ? "المصادر المجانية" : "المكتبة"} مع علامات القراءة الخاصة بها.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel data-testid="admin-bulk-delete-cancel">إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={removeSelected} className="bg-red-800 hover:bg-red-900" data-testid="admin-bulk-delete-confirm">حذف الكل</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
